/**
 * De meldingslaag die altijd aanstaat.
 *
 * Waarom dit bestaat: op 2026-08-20 bleken vier kernpaden maandenlang stil te
 * falen. Een counselor drukte op "Goedkeuren", de server gaf een 500, en het
 * scherm deed niets — geen melding, geen spinner die bleef hangen, niets. Het
 * dossier zei daarna "goedgekeurd" terwijl er geen brief was verstuurd.
 *
 * De oorzaak was niet dat iemand een `onError` vergat. De oorzaak was dat het
 * per mutatie geregeld moest worden, en dan wordt het vroeg of laat vergeten.
 * Daarom hangt deze afhandeling aan de MutationCache: hij vuurt voor élke
 * mutatie in de applicatie, ook voor mutaties die nog geschreven moeten worden,
 * en een eigen `onError` op een mutatie zet hem niet opzij — die komt er
 * bovenop.
 *
 * Wat er in beeld komt is bewust karig: wat er misging, en een nummer waarmee
 * support het incident terugvindt. Geen technische details, want de mensen die
 * hiermee werken zitten met een cliënt aan tafel.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";

export interface MutatieFout {
  /** Oplopend, zodat dezelfde fout twee keer achter elkaar opnieuw opvalt. */
  id: number;
  i18nKey?: string;
  code: string;
  traceId?: string;
}

/**
 * Codes die de gebruiker al ergens anders in beeld krijgt, of die geen melding
 * verdienen. Een verlopen sessie stuurt door naar het inlogscherm; daar een
 * banner overheen leggen maakt het alleen verwarrender.
 */
const STIL = new Set([
  "ERR_AUTH_TOKEN_EXPIRED",
  "ERR_AUTH_TOKEN_INVALID",
  "ERR_AUTH_REFRESH_REUSED",
]);

let volgnummer = 0;
const luisteraars = new Set<(f: MutatieFout) => void>();

/**
 * Een mutatie die zijn fout zélf al in beeld brengt kan de banner uitzetten met
 * `meta: { foutInBeeld: true }`. Dat is de uitzondering en niet de regel: laat
 * hem aan als je twijfelt, want twee meldingen zijn hinderlijk en nul
 * meldingen zijn gevaarlijk.
 */
export interface MutatieMeta {
  foutInBeeld?: boolean;
}

/** Wordt aangeroepen vanuit de MutationCache in App.tsx. */
export function meldMutatieFout(error: unknown, meta?: MutatieMeta): void {
  if (meta?.foutInBeeld) return;

  const api = error instanceof ApiError ? error : undefined;
  const code = api?.code ?? "ERR_INTERNAL";
  if (STIL.has(code)) return;

  const fout: MutatieFout = {
    id: ++volgnummer,
    i18nKey: api?.i18nKey,
    code,
    traceId: api?.traceId,
  };
  for (const l of luisteraars) l(fout);
}

export function MutationErrorBanner() {
  const { t } = useTranslation();
  const [fout, setFout] = useState<MutatieFout | null>(null);

  useEffect(() => {
    const luister = (f: MutatieFout) => setFout(f);
    luisteraars.add(luister);
    return () => {
      luisteraars.delete(luister);
    };
  }, []);

  // Automatisch wegvallen doet hij niet. Een melding die verdwijnt terwijl
  // iemand naar zijn cliënt kijkt, is geen melding.
  if (!fout) return null;

  const tekst = fout.i18nKey
    ? t(fout.i18nKey, { defaultValue: t("errors.internal") })
    : t("errors.internal");

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-4"
    >
      <div className="flex w-full max-w-2xl items-start gap-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 shadow-lg">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-red-900">{t("errors.mutation.title")}</p>
          <p className="mt-0.5 text-sm text-red-800">{tekst}</p>
          {fout.traceId && (
            <p className="mt-1 font-mono text-xs text-red-700">
              {t("errors.mutation.trace")} {fout.traceId}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setFout(null)}
          className="shrink-0 rounded px-2 py-1 text-sm font-medium text-red-900 hover:bg-red-100"
        >
          {t("errors.mutation.dismiss")}
        </button>
      </div>
    </div>
  );
}
